import { FC, Fragment, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMicrochip, faPaperPlane, faTableList } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import * as assistantService from 'services/assistant';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import AssistantCopyButton from './AssistantCopyButton';
import AssistantFeedback from './AssistantFeedback';
import AssistantLoader from './AssistantLoader';
import AssistantSessionViewer from './AssistantSessionViewer';
import AssistantVoiceBubble from './AssistantVoiceBubble';
import AssistantVoiceButton from './AssistantVoiceButton';
import { VoiceRecording, canRecordVoice } from './voiceRecorder';
import {
  AppliedHotelFilters,
  SEARCH_LOADER_MAX_MS,
  STAFF_BRANCH_ID,
  buildPageSearchContext,
  resolveAssistantOpenPage,
  AppliedFlightFilters,
  canOpenFlightSearch,
  canOpenHotelSearch,
  disableSearchWithActiveFilters,
  dropPendingFilters,
  dropPendingFlightFilters,
  enableSearchWithActiveFilters,
  finishSearchLoading,
  getChat,
  getWidgetOpen,
  extractStarsFilterFromSearchPayload,
  normalizeOpenFlightSearchQuery,
  normalizeOpenHotelSearchQuery,
  parseSetResultLocationPayload,
  queueFilters,
  queueFlightFilters,
  annotateActiveHotelInText,
  getActiveHotelForAssistant,
  getPageFlightsForAssistant,
  getPageHotelsForAssistant,
  resolveBareActiveHotelAction,
  parseAppliedFlightFilters,
  parseAppliedHotelFilters,
  queueFlightCardAction,
  queueHotelCardAction,
  queueResultLocation,
  setMessageFeedback,
  resolveFlightOrdinalsInText,
  resolveHotelOrdinalsInText,
  NAVIGATE_PAGE,
  NAVIGATE_PAGE_PATHS,
  NavigatePageKind,
  SET_RESULT_LOCATION,
  sessionStarts,
  shouldKeepFiltersOnSearch,
  subscribeToChats,
  updateChat,
} from 'utils/assistantChatStore';
import { HOTEL_CARD_ACTION, parseHotelCardActionPayload } from 'components/hotel/Result/hotelCardActions';
import {
  FLIGHT_CARD_ACTION,
  parseFlightCardActionPayload,
} from 'components/flight/Result/flightCardActions';
import {
  getProposalItemsForAssistant,
  bareProposalActionCovered,
  parseProposalActionPayload,
  queueProposalAction,
  resolveBareProposalActions,
  resolveProposalOrdinalsInText,
} from 'components/shared/Proposal/Assistant/proposalActions';
import {
  parseOrdersActionPayload,
  parseSearchOrdersPayload,
  queueOrdersAction,
  queueOrdersSearch,
} from 'components/shared/MyOrders/Assistant/ordersActions';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { getHotelIndex, getKey } from 'utils/hotel';
import { highlightAssistantTargets } from 'utils/assistantHighlight';
import { MainContext } from 'contexts/MainContext';

interface FilterResultAIProps {
  // Where this copy of the chat sits. A reply to a question asked in the floating widget while it is closed is
  // counted as unread; one asked in the left panel's tab never is.
  variant?: 'tab' | 'widget';
}

/**
 * The AI Assistant chat. The conversation itself lives in assistantChatStore, so the tab in the results page's left
 * panel and the floating widget show the same one. What the assistant wants changed on the results page (a new search,
 * filters) is not applied here: it is queued, and AssistantPageBridge inside the results page applies it.
 */
const FilterResultAI: FC<FilterResultAIProps> = ({ variant = 'tab' }) => {
  const { t, i18n } = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user } = useContext(MainContext);
  const { filter, setFilter } = useContext(HotelFilterContext);
  const navigate = useNavigate();
  const location = useLocation();
  const chatKey = hotelIndex ?? 0;
  const { sessionId, aiModel, messages, busy, voiceEnabled, voiceMaxSeconds } = useSyncExternalStore(
    subscribeToChats,
    () => getChat(chatKey),
  );
  const [input, setInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const isStaff = Number(user?.branchId) === STAFF_BRANCH_ID;
  const messagesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  // A session is created once per page; a component that mounts later joins the existing one. After a failed
  // attempt the next mount tries again, starting without the earlier error message.
  useEffect(() => {
    if (getChat(chatKey).sessionId || sessionStarts.has(chatKey)) {
      return;
    }
    sessionStarts.add(chatKey);
    updateChat(chatKey, (chat) => ({ ...chat, messages: chat.messages.filter((m) => m.id !== 'err-session') }));

    assistantService
      .createAssistantSession(i18n.language)
      .then((res: assistantService.CreateSessionResponse) =>
        updateChat(chatKey, (chat) => ({
          ...chat,
          sessionId: res.sessionId,
          aiModel: res.aiModel ?? null,
          // The backend decides who gets the microphone; the browser must also be able to record.
          voiceEnabled: !!res.voiceEnabled && canRecordVoice(),
          voiceMaxSeconds: res.voiceMaxSeconds ?? 60,
        })),
      )
      .catch(() =>
        updateChat(chatKey, (chat) => ({
          ...chat,
          messages: [...chat.messages, { id: 'err-session', role: 'system', text: t('assistant.connection_error') }],
        })),
      )
      .finally(() => sessionStarts.delete(chatKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs the search the assistant opened exactly as the search bar would: remember the chosen place, drop the
  // cached page state, and navigate to the results URL, which makes the page start its own search.
  // When keep-filters is on, this matches pressing the toolbar lock then searching with new params.
  const openHotelSearch = async (
    payload: assistantService.OpenHotelSearchPayload,
    latestFilters?: AppliedHotelFilters | null,
  ) => {
    if (!canOpenHotelSearch(payload)) {
      return;
    }

    highlightAssistantTargets(['search']);

    const query = normalizeOpenHotelSearchQuery(payload);

    const gaKey = getKey('hotelSearchGA', hotelIndex);
    if (payload.googlePlace) {
      sessionStorage.setItem(gaKey, JSON.stringify(payload.googlePlace));
    } else {
      sessionStorage.removeItem(gaKey);
    }

    const keepFilters = shouldKeepFiltersOnSearch(payload);
    if (keepFilters) {
      enableSearchWithActiveFilters(filter);
      if (filter) {
        setFilter({ ...filter, searchWithActiveFilters: true });
      }
    } else {
      disableSearchWithActiveFilters(filter);
      if (filter) {
        setFilter({ ...filter, searchWithActiveFilters: false });
      }
    }

    // Filters changed later in the same turn are the most recent word and replace the ones in the command.
    // Search-bar stars stay at 1; any star preference becomes result-filter stars (3/4/5 chips).
    const filtersFromPayload =
      latestFilters ??
      parseAppliedHotelFilters(payload.appliedFilters as Record<string, unknown> | null | undefined);
    const starsFilter = extractStarsFilterFromSearchPayload(payload);
    const filters =
      starsFilter && !(filtersFromPayload && Array.isArray(filtersFromPayload.stars))
        ? { ...(filtersFromPayload || {}), stars: starsFilter }
        : filtersFromPayload;
    if (filters) {
      // The page clears its filter when the search starts, so these wait for the new results.
      queueFilters(chatKey, filters, true);
    } else if (!keepFilters) {
      dropPendingFilters(chatKey);
    }

    await idb.deletePageStateByPartialKey('hotels');
    const search = new URLSearchParams({ ...query, refreshDate: String(Date.now()) }).toString();
    navigate({ pathname: `/hotels${getHotelIndex(hotelIndex)}/results`, search });
  };

  const openFlightSearch = async (
    payload: assistantService.OpenFlightSearchPayload,
    latestFilters?: AppliedFlightFilters | null,
  ) => {
    if (!canOpenFlightSearch(payload)) {
      return;
    }

    highlightAssistantTargets(['search']);

    const query = normalizeOpenFlightSearchQuery(payload);
    const filters =
      latestFilters ??
      parseAppliedFlightFilters(payload.appliedFilters as Record<string, unknown> | null | undefined);
    if (filters) {
      queueFlightFilters(chatKey, filters, true);
    } else {
      dropPendingFlightFilters(chatKey);
    }

    await idb.deletePageStateByPartialKey('flights');
    const search = new URLSearchParams(query).toString();
    navigate({ pathname: '/flights/results', search });
  };

  // Sends one message the way the user typed it: the text is improved on the page first (ordinals, the active hotel,
  // proposal items) and read again after the answer. The text of a voice message goes through here too, which is why the
  // audio is turned into text before it is sent. voiceMessageId is the bubble a voice message already has: it gets the
  // transcript instead of a second bubble. The callers check busy.
  const sendText = async (rawText: string, voiceMessageId?: string) => {
    const text = rawText.trim();
    if (!text || !sessionId) {
      return;
    }

    const hotelsOnPage = getPageHotelsForAssistant(chatKey);
    const onHotelResults = /\/hotels?\d*\/results(\/|$)/i.test(location.pathname);
    const textForBackend = resolveProposalOrdinalsInText(
      resolveFlightOrdinalsInText(
        annotateActiveHotelInText(
          resolveHotelOrdinalsInText(text, hotelsOnPage),
          hotelsOnPage,
          getActiveHotelForAssistant(chatKey),
          onHotelResults,
        ),
        getPageFlightsForAssistant(chatKey),
      ),
      getProposalItemsForAssistant(),
    );

    // Going on with the conversation ends the loader of an earlier search.
    finishSearchLoading(chatKey);
    updateChat(chatKey, (chat) => ({
      ...chat,
      busy: true,
      // A voice message already has its bubble: it gets the transcript instead of a second bubble.
      messages: voiceMessageId
        ? chat.messages.map((m) => (m.id === voiceMessageId ? { ...m, text } : m))
        : [...chat.messages, { id: 'u-' + Date.now(), role: 'user', text }],
    }));

    try {
      const pageSearch = buildPageSearchContext(location.pathname, location.search);
      const openPage = resolveAssistantOpenPage(location.pathname);
      const res = await assistantService.sendAssistantMessage(sessionId, textForBackend, {
        pageSearch,
        searchId: pageSearch?.searchToken || undefined,
        openPage,
      });

      const commands: assistantService.AssistantUiCommand[] = res.uiCommands ?? [];
      const openHotel = commands.find(
        (command: assistantService.AssistantUiCommand) => command.type === assistantService.OPEN_HOTEL_SEARCH,
      );
      const openFlight = commands.find(
        (command: assistantService.AssistantUiCommand) => command.type === assistantService.OPEN_FLIGHT_SEARCH,
      );
      const openOrders = commands.find((command: assistantService.AssistantUiCommand) =>
        assistantService.isSearchOrdersCommand(command.type),
      );
      const ordersSearch = openOrders
        ? parseSearchOrdersPayload(openOrders.payload as Record<string, unknown> | null | undefined)
        : null;
      const opensHotelSearch = canOpenHotelSearch(
        openHotel?.payload as unknown as assistantService.OpenHotelSearchPayload,
      );
      const opensFlightSearch = canOpenFlightSearch(
        openFlight?.payload as unknown as assistantService.OpenFlightSearchPayload,
      );
      const opensOrdersSearch = !!openOrders && !!ordersSearch;
      const opensSearch = opensHotelSearch || opensFlightSearch || opensOrdersSearch;

      // The assistant said it opened a search but the command has no place to open: say so rather than show its
      // "Loading..." for a search that will never start.
      const searchFailedToOpen =
        (!!openHotel && !opensHotelSearch) || (!!openFlight && !opensFlightSearch);
      const widgetClosed = variant === 'widget' && !getWidgetOpen();
      const activeHotel = getActiveHotelForAssistant(chatKey);
      const bareActiveHotelAction = activeHotel
        ? resolveBareActiveHotelAction(text, hotelsOnPage, onHotelResults)
        : null;
      const addActiveHotel =
        !!activeHotel &&
        bareActiveHotelAction?.action === 'add-to-proposal' &&
        !commands.some((command: assistantService.AssistantUiCommand) => {
          if (command.type !== HOTEL_CARD_ACTION) return false;
          return (
            parseHotelCardActionPayload(command.payload as Record<string, unknown> | null | undefined)
              ?.action === 'add-to-proposal'
          );
        });

      updateChat(chatKey, (chat) => ({
        ...chat,
        aiModel: res.aiModel ?? chat.aiModel,
        unread: widgetClosed ? chat.unread + 1 : chat.unread,
        messages: [
          ...chat.messages,
          searchFailedToOpen
            ? { id: res.messageId, role: 'system', text: t('assistant.search_failed') }
            : {
              id: res.messageId,
              role: 'assistant',
              text: addActiveHotel && activeHotel
                ? `Added the active hotel (${activeHotel.name}) to the proposal.`
                : res.message,
              // A reply the server wrote itself is not stored, so there is nothing a rating could point at.
              feedbackAllowed: res.feedbackAllowed !== false,
              ...(opensSearch ? { search: { loading: true } } : {}),
            },
        ],
      }));
      if (opensSearch) {
        setTimeout(() => finishSearchLoading(chatKey, res.messageId), SEARCH_LOADER_MAX_MS);
      }

      const hotelFilterCommands = commands.filter((command: assistantService.AssistantUiCommand) =>
        assistantService.isApplyHotelFiltersCommand(command.type),
      );
      const flightFilterCommands = commands.filter((command: assistantService.AssistantUiCommand) =>
        assistantService.isApplyFlightFiltersCommand(command.type),
      );
      const changeHotelFilters = hotelFilterCommands[hotelFilterCommands.length - 1];
      const changeFlightFilters = flightFilterCommands[flightFilterCommands.length - 1];
      const changedHotelFilters = parseAppliedHotelFilters(
        changeHotelFilters?.payload as Record<string, unknown> | null | undefined,
      );
      const changedFlightFilters = parseAppliedFlightFilters(
        changeFlightFilters?.payload as Record<string, unknown> | null | undefined,
      );

      const cardCommands = commands.filter(
        (command: assistantService.AssistantUiCommand) => command.type === HOTEL_CARD_ACTION,
      );
      const modelDidBareAction = cardCommands.some((command: assistantService.AssistantUiCommand) => {
        const payload = parseHotelCardActionPayload(
          command.payload as Record<string, unknown> | null | undefined,
        );
        return payload?.action === bareActiveHotelAction?.action;
      });
      cardCommands.forEach((command: assistantService.AssistantUiCommand) => {
        const payload = parseHotelCardActionPayload(
          command.payload as Record<string, unknown> | null | undefined,
        );
        if (!payload) return;
        queueHotelCardAction(
          chatKey,
          bareActiveHotelAction && activeHotel
            ? {
                ...payload,
                hotelKey: activeHotel.htuKey,
                hotelName: activeHotel.name,
                index: null,
              }
            : payload,
        );
      });
      if (bareActiveHotelAction && activeHotel && !modelDidBareAction) {
        queueHotelCardAction(chatKey, {
          action: bareActiveHotelAction.action,
          hotelKey: activeHotel.htuKey,
          hotelName: activeHotel.name,
          ...(bareActiveHotelAction.pinned != null ? { pinned: bareActiveHotelAction.pinned } : {}),
          ...(bareActiveHotelAction.show != null ? { show: bareActiveHotelAction.show } : {}),
        });
      }

      const flightCardCommands = commands.filter(
        (command: assistantService.AssistantUiCommand) =>
          command.type === FLIGHT_CARD_ACTION ||
          command.type === assistantService.FLIGHT_CARD_ACTION,
      );
      flightCardCommands.forEach((command: assistantService.AssistantUiCommand) => {
        const payload = parseFlightCardActionPayload(
          command.payload as Record<string, unknown> | null | undefined,
        );
        if (payload) {
          queueFlightCardAction(chatKey, payload);
        }
      });

      const locationCommand = commands.find(
        (command: assistantService.AssistantUiCommand) => command.type === SET_RESULT_LOCATION,
      );
      if (locationCommand) {
        const locationPayload = parseSetResultLocationPayload(
          locationCommand.payload as Record<string, unknown> | null | undefined,
        );
        if (locationPayload) {
          queueResultLocation(chatKey, locationPayload);
        }
      }

      const proposalCommands = commands.filter((command: assistantService.AssistantUiCommand) =>
        assistantService.isProposalActionCommand(command.type),
      );
      const proposalPayloads = proposalCommands
        .map((command: assistantService.AssistantUiCommand) =>
          parseProposalActionPayload(command.payload as Record<string, unknown> | null | undefined),
        )
        .filter((payload): payload is NonNullable<typeof payload> => !!payload);
      proposalPayloads.forEach(queueProposalAction);

      const bareProposalActions = resolveBareProposalActions(text).filter(
        (action) => !bareProposalActionCovered(action, proposalPayloads),
      );
      bareProposalActions.forEach(queueProposalAction);

      const orderActions = commands
        .filter((command: assistantService.AssistantUiCommand) =>
          assistantService.isOrderActionCommand(command.type),
        )
        .map((command: assistantService.AssistantUiCommand) =>
          parseOrdersActionPayload(command.payload as Record<string, unknown> | null | undefined),
        )
        .filter((action): action is NonNullable<typeof action> => !!action);
      orderActions.forEach(queueOrdersAction);

      const page = (
        commands.find(
          (command: assistantService.AssistantUiCommand) => command.type === NAVIGATE_PAGE,
        )?.payload as { page?: string } | undefined
      )?.page;
      if (page && NAVIGATE_PAGE_PATHS[page as NavigatePageKind]) {
        navigate(NAVIGATE_PAGE_PATHS[page as NavigatePageKind]);
      }
      if ((opensOrdersSearch || orderActions.length) && !/^\/my-orders$/i.test(location.pathname)) {
        navigate('/my-orders');
      }
      if (
        (proposalPayloads.length || bareProposalActions.length) &&
        !opensOrdersSearch &&
        !orderActions.length &&
        !location.pathname.startsWith('/proposal')
      ) {
        navigate('/proposal');
      }

      const hotelFiltersToApply = opensOrdersSearch
        ? null
        : changedHotelFilters ||
          (!opensFlightSearch
            ? parseAppliedHotelFilters(res.appliedFilters as Record<string, unknown> | null | undefined)
            : null);
      const flightFiltersToApply = opensOrdersSearch
        ? null
        : changedFlightFilters ||
          parseAppliedFlightFilters(
            (openFlight?.payload as assistantService.OpenFlightSearchPayload | undefined)
              ?.appliedFilters as Record<string, unknown> | null | undefined,
          ) ||
          (opensFlightSearch || location.pathname.startsWith('/flights')
            ? parseAppliedFlightFilters(
                res.appliedFilters as Record<string, unknown> | null | undefined,
              )
            : null);

      if (opensOrdersSearch && ordersSearch) {
        queueOrdersSearch(ordersSearch);
      } else if (opensFlightSearch && openFlight?.payload) {
        await openFlightSearch(
          openFlight.payload as unknown as assistantService.OpenFlightSearchPayload,
          flightFiltersToApply,
        );
      } else if (openHotel?.payload) {
        await openHotelSearch(
          openHotel.payload as unknown as assistantService.OpenHotelSearchPayload,
          hotelFiltersToApply,
        );
      } else if (flightFiltersToApply) {
        queueFlightFilters(chatKey, flightFiltersToApply);
      } else if (hotelFiltersToApply) {
        queueFilters(chatKey, hotelFiltersToApply);
      }
    } catch (err) {
      updateChat(chatKey, (chat) => ({
        ...chat,
        messages: [...chat.messages, { id: 'err-' + Date.now(), role: 'system', text: t('assistant.send_error') }],
      }));
    } finally {
      updateChat(chatKey, (chat) => ({ ...chat, busy: false }));
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !sessionId || busy) {
      return;
    }
    setInput('');
    await sendText(text);
  };

  const failVoice = (text: string) =>
    updateChat(chatKey, (chat) => ({
      ...chat,
      busy: false,
      messages: [...chat.messages, { id: 'err-' + Date.now(), role: 'system', text }],
    }));

  // A voice message: its bubble appears at once (a player on the recording, kept in memory), the audio is turned into
  // text, and the text takes the same path as typed text. A failure leaves the bubble, so it can still be played.
  const handleVoice = async (voice: VoiceRecording) => {
    if (!sessionId || busy) {
      return;
    }

    const id = 'v-' + Date.now();
    finishSearchLoading(chatKey);
    updateChat(chatKey, (chat) => ({
      ...chat,
      busy: true,
      messages: [
        ...chat.messages,
        { id, role: 'user', text: '', audio: { url: URL.createObjectURL(voice.blob), durationMs: voice.durationMs } },
      ],
    }));

    const result = await assistantService.transcribeAssistantVoice(voice.blob);
    if (!result.ok) {
      failVoice(t(result.reason === 'limit' ? 'assistant.voice.limit' : 'assistant.voice.error'));
      return;
    }
    if (!result.text.trim()) {
      failVoice(t('assistant.voice.not_understood'));
      return;
    }

    await sendText(result.text, id);
  };

  return (
    <div className="assistant-chat">
      {sessionId && (
        <div className="assistant-chat-header">
          <div className="assistant-chat-session text-sm" title={sessionId}>
            <span className="assistant-chat-session-label">{t('assistant.session')}</span>
            <code className="assistant-chat-session-id">{sessionId}</code>
            <AssistantCopyButton
              text={sessionId}
              label={t('assistant.copy_session_id')}
              copiedLabel={t('assistant.copied')}
            />
          </div>
          {isStaff && (
            <div className="assistant-chat-staff text-xsm">
              {aiModel && (
                <span className="assistant-chat-model" title={t('assistant.model')}>
                  <FontAwesomeIcon icon={faMicrochip} />
                  <span>{aiModel}</span>
                </span>
              )}
              <button type="button" className="assistant-chat-viewer-btn" onClick={() => setViewerOpen(true)}>
                <FontAwesomeIcon icon={faTableList} />
                <span>{t('assistant.session_viewer')}</span>
              </button>
            </div>
          )}
        </div>
      )}
      {viewerOpen && isStaff && <AssistantSessionViewer onClose={() => setViewerOpen(false)} />}
      <div className="assistant-chat-messages text-xsm" ref={messagesRef}>
        {!messages.length && <div className="assistant-chat-empty">{t('assistant.greeting')}</div>}
        {messages.map((message) => {
          if (message.audio) {
            return <AssistantVoiceBubble key={message.id} audio={message.audio} transcript={message.text} />;
          }

          const bubble = message.search ? (
            <div className="assistant-chat-bubble assistant-chat-bubble--assistant assistant-chat-bubble--loading">
              {message.search.loading ? (
                <AssistantLoader />
              ) : (
                <span className="assistant-chat-done">
                  <FontAwesomeIcon icon={faCheck} />
                  {t('assistant.results_opened')}
                </span>
              )}
            </div>
          ) : (
            <div className={`assistant-chat-bubble assistant-chat-bubble--${message.role}`}>{message.text}</div>
          );

          // Every stored answer can be rated, a search reply once its loader is gone; the replies the server writes
          // itself, the user's own messages and the system notes cannot.
          const canRate =
            !!sessionId &&
            message.role === 'assistant' &&
            message.feedbackAllowed !== false &&
            !message.search?.loading;
          if (!canRate) {
            return <Fragment key={message.id}>{bubble}</Fragment>;
          }

          return (
            <div key={message.id} className="assistant-chat-answer">
              {bubble}
              <AssistantFeedback
                sessionId={sessionId}
                messageId={message.id}
                feedback={message.feedback}
                onSaved={(saved) => setMessageFeedback(chatKey, message.id, saved)}
              />
            </div>
          );
        })}
        {busy && (
          <div className="assistant-chat-bubble assistant-chat-bubble--assistant assistant-chat-bubble--loading">
            <AssistantLoader />
          </div>
        )}
      </div>
      <div className="assistant-chat-composer">
        <input
          className="assistant-chat-input"
          dir={/[\u0590-\u05FF]/.test(input) ? 'rtl' : 'ltr'}
          value={input}
          placeholder={
            recording
              ? t('assistant.voice.recording')
              : sessionId
              ? t('assistant.placeholder')
              : t('assistant.connecting')
          }
          disabled={!sessionId || busy || recording}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSend();
            }
          }}
        />
        {voiceEnabled && (
          <AssistantVoiceButton
            disabled={!sessionId || busy}
            maxSeconds={voiceMaxSeconds}
            onRecordingChange={setRecording}
            onRecorded={handleVoice}
            onError={(kind) =>
              failVoice(t(kind === 'denied' ? 'assistant.voice.mic_denied' : 'assistant.voice.unsupported'))
            }
          />
        )}
        {/* While recording, the paper plane of the recording controls is the one way to send; two would confuse. */}
        {!recording && (
          <Button
            title={t('assistant.send')}
            icon={faPaperPlane}
            className="btn btn-primary assistant-chat-send"
            disabled={!sessionId || busy || !input.trim()}
            onClick={handleSend}
          />
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(FilterResultAI, 'FilterResultAI');
